import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { Auth } from "shared/@auth/Auth";
import Checkbox from "shared/@forms/Checkbox";
import Input from "shared/@forms/Input";
import Select from "shared/@forms/Select";
import AlertNotice from "shared/AlertNotice";
import Button from "shared/Button";
import { Modal } from "shared/Modal";
import ModalActionBar from "shared/ModalActionBar";
import { ENVIRONMENT_CONFIG } from "shared/globals";

import {
  lookupSupportSettings,
  revokeSupportSessions,
  SUPPORT_LANGUAGES,
  updateSupportSettings,
} from "./supportOperationsApi";
import styles from "./SupportAccountOperations.module.css";

const SETTINGS_READ_PERMISSION = "membership.dreamcast.usersettings.read";
const SETTINGS_UPDATE_PERMISSION = "membership.dreamcast.usersettings.update";
const SESSION_REVOCATION_PERMISSION = "membership.dreamcast.sessions.revoke";

function emptySettingsState() {
  return {
    isOpen: false,
    loading: false,
    loaded: null,
    draft: null,
    error: null,
    success: null,
    confirming: false,
    saving: false,
  };
}

function emptyRevocationState() {
  return {
    isOpen: false,
    confirmation: "",
    pending: false,
    error: null,
    revokedSessionCount: null,
  };
}

function mapSettingsError(status, invalidPayload) {
  if (invalidPayload) {
    return "DreamCast returned an invalid settings response. Please retry.";
  }
  if (status === 401 || status === 403) {
    return "You are not authorized to manage these settings.";
  }
  if (status === 404) {
    return "DreamCast settings are unavailable for this contact.";
  }
  if (status === 429) {
    return "Too many settings requests. Please try again in a moment.";
  }
  return "DreamCast settings are unavailable right now.";
}

function mapRevocationError(status, invalidPayload) {
  if (invalidPayload) {
    return "DreamCast returned an invalid session response. Please retry.";
  }
  if (status === 401 || status === 403) {
    return "You are not authorized to force logout this contact.";
  }
  if (status === 404) {
    return "DreamCast sessions are unavailable for this contact.";
  }
  if (status === 429) {
    return "Too many force logout requests. Please try again in a moment.";
  }
  return "DreamCast session revocation is unavailable right now.";
}

export function SupportAccountOperations({
  contactKey,
  heading,
  contact,
  iboNumber,
  accountIsAuthoritative,
  hasPermission,
  makeAuthedRequest,
}) {
  const [settingsState, setSettingsState] = useState(emptySettingsState);
  const [revocationState, setRevocationState] = useState(emptyRevocationState);
  const mountedRef = useRef(true);
  const requestGenerationRef = useRef(0);

  const email =
    typeof contact?.email === "string"
      ? contact.email.trim().toLowerCase()
      : "";
  const identity = `${iboNumber}|${contactKey}|${contact?.role || ""}|${email}`;
  const belongsToActiveIbo =
    String(contact?.iboNumber || "").trim() === String(iboNumber || "").trim();
  const hasAvailableAccount =
    belongsToActiveIbo &&
    accountIsAuthoritative &&
    contact?.account?.status === "available";
  const canReadSettings =
    Boolean(ENVIRONMENT_CONFIG?.flags?.supportsDreamCastUserSettingsRead) &&
    hasPermission(SETTINGS_READ_PERMISSION) &&
    hasAvailableAccount &&
    Boolean(email);
  const canUpdateSettings =
    canReadSettings &&
    Boolean(ENVIRONMENT_CONFIG?.flags?.supportsDreamCastUserSettingsUpdate) &&
    hasPermission(SETTINGS_UPDATE_PERMISSION);
  const canRevokeSessions =
    Boolean(ENVIRONMENT_CONFIG?.flags?.supportsDreamCastSessionRevocation) &&
    hasPermission(SESSION_REVOCATION_PERMISSION) &&
    hasAvailableAccount &&
    Boolean(email);
  const contextRef = useRef(null);
  contextRef.current = {
    identity,
    canReadSettings,
    canUpdateSettings,
    canRevokeSessions,
  };

  const invalidateRequests = useCallback(() => {
    requestGenerationRef.current += 1;
  }, []);

  const isRequestCurrent = useCallback(
    (generation, requestIdentity, access) => {
      if (
        !mountedRef.current ||
        requestGenerationRef.current !== generation ||
        contextRef.current.identity !== requestIdentity
      ) {
        return false;
      }
      return Boolean(contextRef.current[access]);
    },
    []
  );

  const closeSettings = useCallback(() => {
    invalidateRequests();
    setSettingsState(emptySettingsState());
  }, [invalidateRequests]);

  const closeRevocation = useCallback(() => {
    invalidateRequests();
    setRevocationState(emptyRevocationState());
  }, [invalidateRequests]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      invalidateRequests();
    };
  }, [invalidateRequests]);

  useEffect(() => {
    closeSettings();
    closeRevocation();
  }, [
    closeRevocation,
    closeSettings,
    identity,
    canReadSettings,
    canUpdateSettings,
    canRevokeSessions,
  ]);

  const loadSettings = async () => {
    if (!canReadSettings) {
      return;
    }
    const generation = requestGenerationRef.current + 1;
    requestGenerationRef.current = generation;
    const requestIdentity = identity;
    setSettingsState({
      ...emptySettingsState(),
      isOpen: true,
      loading: true,
    });

    const result = await lookupSupportSettings(makeAuthedRequest, email);
    if (!isRequestCurrent(generation, requestIdentity, "canReadSettings")) {
      return;
    }
    if (!result.ok) {
      setSettingsState({
        ...emptySettingsState(),
        isOpen: true,
        error: mapSettingsError(result.status, result.invalidPayload),
      });
      return;
    }
    setSettingsState({
      ...emptySettingsState(),
      isOpen: true,
      loaded: result.data,
      draft: result.data,
    });
  };

  const changes = useMemo(() => {
    if (!settingsState.loaded || !settingsState.draft) {
      return {};
    }
    const nextChanges = {};
    if (
      settingsState.draft.receiveNotifications !==
      settingsState.loaded.receiveNotifications
    ) {
      nextChanges.receiveNotifications =
        settingsState.draft.receiveNotifications;
    }
    if (
      settingsState.draft.language &&
      settingsState.draft.language !== settingsState.loaded.language
    ) {
      nextChanges.language = settingsState.draft.language;
    }
    return nextChanges;
  }, [settingsState.draft, settingsState.loaded]);

  const changedLabels = Object.keys(changes).map((key) =>
    key === "receiveNotifications" ? "Notifications" : "Language"
  );

  const saveSettings = async () => {
    if (
      settingsState.saving ||
      !canUpdateSettings ||
      Object.keys(changes).length === 0
    ) {
      return;
    }
    const generation = requestGenerationRef.current + 1;
    requestGenerationRef.current = generation;
    const requestIdentity = identity;
    setSettingsState((previous) => ({
      ...previous,
      saving: true,
      error: null,
      success: null,
    }));

    const result = await updateSupportSettings(
      makeAuthedRequest,
      email,
      changes
    );
    if (!isRequestCurrent(generation, requestIdentity, "canUpdateSettings")) {
      return;
    }
    if (!result.ok) {
      setSettingsState((previous) => ({
        ...previous,
        confirming: false,
        saving: false,
        error: mapSettingsError(result.status, result.invalidPayload),
      }));
      return;
    }
    setSettingsState((previous) => ({
      ...previous,
      loaded: result.data,
      draft: result.data,
      confirming: false,
      saving: false,
      success: "DreamCast settings were updated.",
    }));
  };

  const revokeSessions = async () => {
    if (
      revocationState.pending ||
      revocationState.confirmation !== "REVOKE" ||
      !canRevokeSessions
    ) {
      return;
    }
    const generation = requestGenerationRef.current + 1;
    requestGenerationRef.current = generation;
    const requestIdentity = identity;
    setRevocationState((previous) => ({
      ...previous,
      pending: true,
      error: null,
      revokedSessionCount: null,
    }));

    const result = await revokeSupportSessions(makeAuthedRequest, email);
    if (!isRequestCurrent(generation, requestIdentity, "canRevokeSessions")) {
      return;
    }
    if (!result.ok) {
      setRevocationState((previous) => ({
        ...previous,
        pending: false,
        error: mapRevocationError(result.status, result.invalidPayload),
      }));
      return;
    }
    setRevocationState((previous) => ({
      ...previous,
      confirmation: "",
      pending: false,
      revokedSessionCount: result.data.revokedSessionCount,
    }));
  };

  if (!canReadSettings && !canRevokeSessions) {
    return null;
  }

  return (
    <>
      <div className={styles.actions}>
        {canReadSettings && (
          <Button
            size="small"
            onClick={loadSettings}
            aria-label={`Manage settings for ${heading}`}
          >
            Settings
          </Button>
        )}
        {canRevokeSessions && (
          <Button
            size="small"
            purpose="delete"
            onClick={() =>
              setRevocationState({
                ...emptyRevocationState(),
                isOpen: true,
              })
            }
            aria-label={`Force logout ${heading}`}
          >
            Force logout
          </Button>
        )}
      </div>

      <Modal
        title="DreamCast settings"
        isOpen={settingsState.isOpen}
        onClose={closeSettings}
        className={styles.modal}
      >
        <div className={styles.modalContent}>
          <p className={styles.intro}>
            {heading}
            {contact.displayName ? `: ${contact.displayName}` : ""}
            <br />
            Email: <strong>{contact.email}</strong>
          </p>

          {settingsState.loading && (
            <p role="status" aria-live="polite">
              Loading settings...
            </p>
          )}
          {settingsState.error && (
            <AlertNotice title="Unable to load DreamCast settings">
              <div>{settingsState.error}</div>
            </AlertNotice>
          )}
          {settingsState.success && (
            <p role="status" aria-live="polite" className={styles.success}>
              {settingsState.success}
            </p>
          )}

          {settingsState.draft && (
            <div className={styles.form}>
              <label className={styles.checkboxField}>
                <Checkbox
                  checked={settingsState.draft.receiveNotifications}
                  disabled={!canUpdateSettings || settingsState.saving}
                  onChange={(event) =>
                    setSettingsState((previous) => ({
                      ...previous,
                      draft: {
                        ...previous.draft,
                        receiveNotifications: event.target.checked,
                      },
                      success: null,
                    }))
                  }
                />
                <span>Receive notifications</span>
              </label>

              <label className={styles.field}>
                <span>Language</span>
                <Select
                  value={settingsState.draft.language || ""}
                  disabled={!canUpdateSettings || settingsState.saving}
                  onChange={(event) =>
                    setSettingsState((previous) => ({
                      ...previous,
                      draft: {
                        ...previous.draft,
                        language: event.target.value,
                      },
                      success: null,
                    }))
                  }
                >
                  <option value="" disabled>
                    Select a language
                  </option>
                  {SUPPORT_LANGUAGES.map((language) => (
                    <option key={language} value={language}>
                      {language}
                    </option>
                  ))}
                </Select>
              </label>
            </div>
          )}

          <ModalActionBar>
            <Button onClick={closeSettings}>Close</Button>
            {settingsState.error && (
              <Button onClick={loadSettings}>Retry</Button>
            )}
            {settingsState.draft && canUpdateSettings && (
              <Button
                purpose="save"
                disabled={
                  settingsState.saving || Object.keys(changes).length === 0
                }
                onClick={() =>
                  setSettingsState((previous) => ({
                    ...previous,
                    confirming: true,
                    error: null,
                  }))
                }
              >
                Save
              </Button>
            )}
          </ModalActionBar>
        </div>
      </Modal>

      <Modal
        title="Confirm settings changes"
        isOpen={settingsState.confirming}
        onClose={() =>
          !settingsState.saving &&
          setSettingsState((previous) => ({
            ...previous,
            confirming: false,
          }))
        }
        className={styles.modal}
      >
        <div className={styles.modalContent}>
          <p className={styles.intro}>
            Save changes for {contact.displayName || heading} ({contact.email})?
          </p>
          <div>
            Changed settings:
            <ul className={styles.changeList}>
              {changedLabels.map((label) => (
                <li key={label}>{label}</li>
              ))}
            </ul>
          </div>
          {settingsState.saving && (
            <p role="status" aria-live="polite">
              Saving settings...
            </p>
          )}
          <ModalActionBar>
            <Button
              disabled={settingsState.saving}
              onClick={() =>
                setSettingsState((previous) => ({
                  ...previous,
                  confirming: false,
                }))
              }
            >
              Cancel
            </Button>
            <Button
              purpose="save"
              disabled={settingsState.saving}
              onClick={saveSettings}
            >
              Confirm save
            </Button>
          </ModalActionBar>
        </div>
      </Modal>

      <Modal
        title="Force logout"
        isOpen={revocationState.isOpen}
        onClose={closeRevocation}
        className={styles.modal}
      >
        <div className={styles.modalContent}>
          <p className={styles.intro}>
            Force logout {contact.displayName || heading} ({contact.email})?
          </p>
          <p>
            This ends every active DreamCast app session for this contact. The
            customer can sign in again through the normal login flow.
          </p>

          {revocationState.revokedSessionCount === null && (
            <label className={styles.field}>
              <span>
                Type <strong>REVOKE</strong> to continue
              </span>
              <Input
                value={revocationState.confirmation}
                autoComplete="off"
                disabled={revocationState.pending}
                onChange={(event) =>
                  setRevocationState((previous) => ({
                    ...previous,
                    confirmation: event.target.value,
                    error: null,
                  }))
                }
              />
            </label>
          )}

          {revocationState.pending && (
            <p role="status" aria-live="polite">
              Revoking sessions...
            </p>
          )}
          {revocationState.error && (
            <AlertNotice title="Unable to force logout">
              <div>{revocationState.error}</div>
            </AlertNotice>
          )}
          {revocationState.revokedSessionCount !== null && (
            <AlertNotice title="Sessions revoked">
              <div>
                Revoked sessions: {revocationState.revokedSessionCount}.
              </div>
              <div>
                The customer can sign in again through the normal login flow.
              </div>
            </AlertNotice>
          )}

          <ModalActionBar>
            <Button onClick={closeRevocation}>Close</Button>
            {revocationState.revokedSessionCount === null && (
              <Button
                purpose="delete"
                disabled={
                  revocationState.pending ||
                  revocationState.confirmation !== "REVOKE"
                }
                onClick={revokeSessions}
              >
                Revoke all sessions
              </Button>
            )}
          </ModalActionBar>
        </div>
      </Modal>
    </>
  );
}

export default function SupportAccountOperationsWithAuth(props) {
  const { hasPermission, makeAuthedRequest } = Auth.useContainer();

  return (
    <SupportAccountOperations
      {...props}
      hasPermission={hasPermission}
      makeAuthedRequest={makeAuthedRequest}
    />
  );
}
